import AdminRequestManagement from 'pages/admin-request-management/Index';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes as RouterRoutes,
  useLocation,
} from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { useGetDetailsQuery } from 'redux/features/users/authApi';

import ProtectedRoute from 'components/ProtectedRoute';
import EmployeeProfile from 'pages/employee-profile';
import NotFound from 'pages/NotFound';
import SocketEventsListener from 'components/SocketEventsListener';
import EmployeeRequests from 'pages/employee-requests';
import EmployeeRules from 'pages/employee-rules';
import EmployeeManagement from 'pages/employee-management';
import AttendanceAndSalary from 'pages/salary';
import EmployeeBonds from 'pages/employee-bonds';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';

// Page imports
import AdminDashboard from './pages/admin-dashboard';
import AdminLeaveManagement from './pages/admin-leave-management';
import AdminSettings from './pages/admin-settings';
import AnalyticsDashboard from './pages/analytics-dashboard';
import DashboardOverview from './pages/dashboard-overview';
import EmployeeEditFullPage from './pages/employee-edit-full-page';
import EmployeeTimeLogs from './pages/employee-time-logs';
import HolidayManagementAdminView from './pages/holiday-management-admin-view';
import HolidayManagementEmployeeView from './pages/holiday-management-employee-view';
import KanbanBoard from './pages/kanban-board';
import LoginRegister from './pages/login-register';
import SprintPlanning from './pages/sprint-planning';
import TaskDetail from './pages/task-detail';
import TeamManagement from './pages/team-management';

const AppShell = () => {
  // Skip auth check only on truly public routes like /verify/*
  const { data: userData, isLoading, isSuccess } = useGetDetailsQuery(undefined);

  // Show loading spinner while checking auth (except on verify routes)
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96 ">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Redirect logged-in users away from login pages to their dashboard
  const userRole = userData?.role;
  const isAuthenticated = isSuccess && userData;

  const getLoginElement = () => {
    if (isAuthenticated) {
      const redirectTo = userRole === 'ADMIN' ? '/admin-dashboard' : '/dashboard-overview';
      return <Navigate to={redirectTo} replace />;
    }
    return <LoginRegister />;
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <ErrorBoundary>
        <SocketEventsListener />
        <ScrollToTop />
        <RouterRoutes>
          <Route path="/login-register" element={getLoginElement()} />
          <Route
            path="/dashboard-overview"
            element={
              <ProtectedRoute allowedRoles={['EMPLOYEE']}>
                <DashboardOverview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employee-profile"
            element={
              <ProtectedRoute allowedRoles={['EMPLOYEE']}>
                <EmployeeProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employee-rules"
            element={
              <ProtectedRoute allowedRoles={['EMPLOYEE', 'ADMIN']}>
                <EmployeeRules />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-dashboard"
            element={
              <ProtectedRoute allowedRoles={['ADMIN']}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employee-management"
            element={
              <ProtectedRoute allowedRoles={['ADMIN']}>
                <EmployeeManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-leave-management"
            element={
              <ProtectedRoute allowedRoles={['ADMIN']}>
                <AdminLeaveManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-request-management"
            element={
              <ProtectedRoute allowedRoles={['ADMIN']}>
                <AdminRequestManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-settings"
            element={
              <ProtectedRoute allowedRoles={['ADMIN', 'EMPLOYEE']}>
                <AdminSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employee-requests"
            element={
              <ProtectedRoute allowedRoles={['EMPLOYEE']}>
                <EmployeeRequests />
              </ProtectedRoute>
            }
          />
          {/* Employee detail pages live under /employee-management; the old paths still redirect */}
          <Route
            path="/employee-edit-full-page"
            element={<RedirectKeepingQuery to="/employee-management/employee-edit-full-page" />}
          />
          <Route
            path="/employee-time-logs"
            element={<RedirectKeepingQuery to="/employee-management/employee-time-logs" />}
          />
          <Route
            path="/employee-management/employee-edit-full-page"
            element={
              <ProtectedRoute allowedRoles={['ADMIN']}>
                <EmployeeEditFullPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employee-management/employee-time-logs"
            element={
              <ProtectedRoute allowedRoles={['ADMIN']}>
                <EmployeeTimeLogs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/holiday-management-admin-view"
            element={
              <ProtectedRoute allowedRoles={['ADMIN']}>
                <HolidayManagementAdminView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/holiday-management-employee-view"
            element={
              <ProtectedRoute allowedRoles={['EMPLOYEE']}>
                <HolidayManagementEmployeeView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/attendance-salary"
            element={
              <ProtectedRoute allowedRoles={['ADMIN']}>
                <AttendanceAndSalary />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bonds"
            element={
              <ProtectedRoute allowedRoles={['ADMIN']}>
                <EmployeeBonds />
              </ProtectedRoute>
            }
          />
          <Route path="/kanban-board" element={<KanbanBoard />} />
          <Route path="/sprint-planning" element={<SprintPlanning />} />
          <Route path="/task-detail" element={<TaskDetail />} />
          <Route path="/analytics-dashboard" element={<AnalyticsDashboard />} />
          <Route path="/team-management" element={<TeamManagement />} />
          <Route path="/" element={getLoginElement()} />
          <Route path="*" element={<NotFound />} />
        </RouterRoutes>
      </ErrorBoundary>
    </>
  );
};

// Redirect to another path, keeping ?query and #hash
const RedirectKeepingQuery = ({ to }) => {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
};

const Routes = () => (
  <BrowserRouter>
    <AppShell />
  </BrowserRouter>
);

export default Routes;
